/* ----------------------------------------------------------------------------
 * AI Content Studio — media data sources. Stock (Pexels) search/save + own-media
 * upload/list. One mock/real gate per fetcher (USE_MOCK_DATA ? mock : api). Real
 * mode targets the locked BC endpoints; the browser NEVER calls a provider
 * directly and never sees a key.
 *
 * HONESTY INVARIANTS:
 *  - Search reads are free GETs. Stock save is an explicit write (server resolves
 *    the download URL from provider+id+rendition — the client never sends a URL).
 *  - Own-media upload sends the user's raw bytes to POST /v1/media/upload; the
 *    server validates first-hand and its rejection is surfaced verbatim.
 *  - Adapters are null-honest: an absent field stays null, never fabricated.
 *
 * ISOLATION INVARIANT:
 *  - No fetcher sends a workspace/domain parameter — the server scopes every
 *    read+write to the session's workspace. fetchOwnMedia additionally narrows
 *    to source='upload' but can NEVER widen beyond the server-scoped list.
 * -------------------------------------------------------------------------- */

import { api, USE_MOCK_DATA, ApiError } from '@/lib/api';
import type {
  MediaKind,
  MediaSource,
  SavedAsset,
  SavedAssetList,
  SaveResult,
  SearchParams,
  StockItem,
  StockRendition,
  StockSearchResult,
} from '@/types/media';

/* ---- null-honest coercion ------------------------------------------------- */
function strOf(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v !== '' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function boolOf(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

/* ---- API DTOs (raw envelope shapes) --------------------------------------- */
interface RenditionDto {
  key?: string | null;
  width?: number | string | null;
  height?: number | string | null;
  quality?: string | null;
  file_type?: string | null;
}
interface StockItemDto {
  provider?: string | null;
  provider_asset_id?: string | number | null;
  kind?: string | null;
  preview_url?: string | null;
  poster_url?: string | null;
  preview_video_url?: string | null;
  renditions?: RenditionDto[] | null;
  photographer?: string | null;
  photographer_url?: string | null;
  source_url?: string | null;
  attribution?: string | null;
  width?: number | string | null;
  height?: number | string | null;
  alt?: string | null;
  duration_secs?: number | string | null;
}
interface SearchDto {
  items?: StockItemDto[];
  page?: number | string | null;
  per_page?: number | string | null;
  total_results?: number | string | null;
  next_page?: boolean | null;
  source?: string | null;
}
interface SavedAssetDto {
  id?: number | string | null;
  provider?: string | null;
  provider_asset_id?: string | number | null;
  kind?: string | null;
  rendition?: string | null;
  source_url?: string | null;
  photographer?: string | null;
  photographer_url?: string | null;
  attribution?: string | null;
  storage_ref?: string | null;
  mime?: string | null;
  bytes?: number | string | null;
  width?: number | string | null;
  height?: number | string | null;
  created_at?: string | null;
  // own-media additions (feature-detected; absent for legacy stock rows)
  source?: string | null;
  original_filename?: string | null;
  preview_url?: string | null;
  thumb_url?: string | null;
}
interface SavedListDto {
  items?: SavedAssetDto[];
  next_cursor?: number | string | null;
  source?: string | null;
}
interface SaveResponseDto {
  asset?: SavedAssetDto;
  deduped?: boolean | null;
}
interface UploadResponseDto {
  asset?: SavedAssetDto;
  deduped?: boolean | null;
}

/* ---- adapters ------------------------------------------------------------- */
export function adaptKind(raw: unknown): MediaKind {
  return raw === 'video' || raw === 'own_video' ? 'video' : 'photo';
}
function adaptSource(raw: unknown, provider: string): MediaSource {
  return raw === 'upload' || provider === 'upload' ? 'upload' : 'stock';
}
function adaptRendition(dto: RenditionDto): StockRendition {
  return {
    key: strOf(dto.key),
    width: numOrNull(dto.width),
    height: numOrNull(dto.height),
    quality: strOrNull(dto.quality),
    fileType: strOrNull(dto.file_type),
  };
}
function adaptItem(dto: StockItemDto): StockItem {
  const kind = adaptKind(dto.kind);
  return {
    provider: strOf(dto.provider, 'pexels'),
    providerAssetId: String(dto.provider_asset_id ?? ''),
    kind,
    previewUrl: strOf(dto.preview_url ?? dto.poster_url),
    previewVideoUrl: strOrNull(dto.preview_video_url),
    renditions: (dto.renditions ?? []).map(adaptRendition).filter((r) => r.key !== ''),
    photographer: strOf(dto.photographer, 'Unknown'),
    photographerUrl: strOrNull(dto.photographer_url),
    sourceUrl: strOrNull(dto.source_url),
    attribution: strOf(dto.attribution, `${kind === 'video' ? 'Video' : 'Photo'} on Pexels`),
    width: numOrNull(dto.width),
    height: numOrNull(dto.height),
    alt: strOrNull(dto.alt),
    durationSecs: numOrNull(dto.duration_secs),
  };
}
function adaptSaved(dto: SavedAssetDto): SavedAsset {
  const provider = strOf(dto.provider, 'pexels');
  return {
    id: numOrNull(dto.id),
    provider,
    providerAssetId: String(dto.provider_asset_id ?? ''),
    kind: adaptKind(dto.kind),
    rendition: strOf(dto.rendition),
    sourceUrl: strOrNull(dto.source_url),
    photographer: strOrNull(dto.photographer),
    photographerUrl: strOrNull(dto.photographer_url),
    attribution: strOrNull(dto.attribution),
    storageRef: strOf(dto.storage_ref),
    mime: strOf(dto.mime),
    bytes: numOrNull(dto.bytes),
    width: numOrNull(dto.width),
    height: numOrNull(dto.height),
    createdAt: strOrNull(dto.created_at),
    source: adaptSource(dto.source, provider),
    originalFilename: strOrNull(dto.original_filename),
    // Only a genuine image URL — never source_url (a provider PAGE for stock).
    previewUrl: strOrNull(dto.preview_url ?? dto.thumb_url),
  };
}

/* ---- MOCKS (dev-only, STRICTLY behind USE_MOCK_DATA) ---------------------- */
const MOCK_PHOTOS: StockItem[] = Array.from({ length: 9 }, (_, i) => ({
  provider: 'pexels',
  providerAssetId: `${8436610 + i}`,
  kind: 'photo',
  previewUrl: `https://images.pexels.com/photos/${8436610 + i}/pexels-photo.jpeg?auto=compress&cs=tinysrgb&w=600`,
  previewVideoUrl: null,
  renditions: [
    { key: 'large', width: 1880, height: 1253, quality: null, fileType: null },
    { key: 'original', width: 6000, height: 4000, quality: null, fileType: null },
  ],
  photographer: ['Yan Krukau', 'Klaus Nielsen', 'Elina Fairytale'][i % 3],
  photographerUrl: 'https://www.pexels.com/@yankrukov',
  sourceUrl: `https://www.pexels.com/photo/${8436610 + i}/`,
  attribution: `Photo by ${['Yan Krukau', 'Klaus Nielsen', 'Elina Fairytale'][i % 3]} on Pexels`,
  width: 6000,
  height: 4000,
  alt: 'People in a yoga class',
  durationSecs: null,
}));
const MOCK_VIDEOS: StockItem[] = Array.from({ length: 6 }, (_, i) => ({
  provider: 'pexels',
  providerAssetId: `${8480625 + i}`,
  kind: 'video',
  previewUrl: `https://images.pexels.com/videos/${8480625 + i}/free-video.jpg?auto=compress&cs=tinysrgb&w=600`,
  previewVideoUrl: `https://videos.pexels.com/video-files/${8480625 + i}/${8480625 + i}-sd_640_360_25fps.mp4`,
  renditions: [
    { key: 'sd_640x360', width: 640, height: 360, quality: 'sd', fileType: 'video/mp4' },
    { key: 'hd_1920x1080', width: 1920, height: 1080, quality: 'hd', fileType: 'video/mp4' },
  ],
  photographer: 'Yan Krukau',
  photographerUrl: 'https://www.pexels.com/@yankrukov',
  sourceUrl: `https://www.pexels.com/video/${8480625 + i}/`,
  attribution: 'Video by Yan Krukau on Pexels',
  width: 1920,
  height: 1080,
  alt: null,
  durationSecs: 19,
}));
let MOCK_SAVED: SavedAsset[] = [];
let mockSeq = 5000;

/* ---- fetchers ------------------------------------------------------------- */
export async function searchStock(params: SearchParams): Promise<StockSearchResult> {
  const { query, kind, page = 1, perPage = 24, orientation = null } = params;
  if (USE_MOCK_DATA) {
    const all = kind === 'video' ? MOCK_VIDEOS : MOCK_PHOTOS;
    return { items: query.trim() === '' ? [] : all, page, perPage, totalResults: all.length, nextPage: false, source: 'pexels' };
  }
  const path = kind === 'video' ? '/media/stock/videos' : '/media/stock/photos';
  const res = await api.get<SearchDto>(path, {
    query,
    page,
    per_page: perPage,
    orientation: orientation ?? undefined,
  });
  return {
    items: (res.items ?? []).map(adaptItem),
    page: numOrNull(res.page) ?? page,
    perPage: numOrNull(res.per_page) ?? perPage,
    totalResults: numOrNull(res.total_results) ?? 0,
    nextPage: boolOf(res.next_page),
    source: strOf(res.source, 'pexels'),
  };
}

export async function saveStockAsset(item: StockItem, rendition: string): Promise<SaveResult> {
  if (USE_MOCK_DATA) {
    mockSeq += 1;
    const existing = MOCK_SAVED.find(
      (a) => a.providerAssetId === item.providerAssetId && a.rendition === rendition,
    );
    if (existing) return { asset: existing, deduped: true };
    const asset: SavedAsset = {
      id: mockSeq,
      provider: item.provider,
      providerAssetId: item.providerAssetId,
      kind: item.kind,
      rendition,
      sourceUrl: item.sourceUrl,
      photographer: item.photographer,
      photographerUrl: item.photographerUrl,
      attribution: item.attribution,
      storageRef: `workspace-mock/${item.provider}_${item.kind}_${item.providerAssetId}_${rendition}`,
      mime: item.kind === 'video' ? 'video/mp4' : 'image/jpeg',
      bytes: 128000,
      width: item.width,
      height: item.height,
      createdAt: new Date().toISOString(),
      source: 'stock',
      originalFilename: null,
      previewUrl: item.previewUrl || null,
    };
    MOCK_SAVED = [asset, ...MOCK_SAVED];
    return { asset, deduped: false };
  }
  const res = await api.post<SaveResponseDto>('/media/stock/save', {
    provider: item.provider,
    provider_asset_id: item.providerAssetId,
    rendition,
    kind: item.kind,
  });
  return { asset: adaptSaved(res.asset ?? {}), deduped: boolOf(res.deduped) };
}

export async function fetchSavedAssets(): Promise<SavedAssetList> {
  if (USE_MOCK_DATA) {
    return { items: [...MOCK_SAVED], nextCursor: null, source: 'bc_media_assets' };
  }
  const res = await api.get<SavedListDto>('/media/assets');
  return {
    items: (res.items ?? []).map(adaptSaved),
    nextCursor: numOrNull(res.next_cursor),
    source: strOf(res.source, 'bc_media_assets'),
  };
}

/* ---- own-media (My uploads) ----------------------------------------------
 * List the caller's OWN workspace uploads. `source=upload` is a server hint; we
 * additionally narrow client-side to source/provider='upload' so a backend that
 * ignores the hint still yields own-only rows. Never sends a workspace param —
 * the server scopes to the session workspace and the narrowing can only shrink
 * that list, never reach another workspace/domain.
 * -------------------------------------------------------------------------- */
export async function fetchOwnMedia(cursor: number | null = null, limit = 40): Promise<SavedAssetList> {
  if (USE_MOCK_DATA) {
    const uploads = MOCK_SAVED.filter((a) => a.source === 'upload' || a.provider === 'upload');
    return { items: uploads, nextCursor: null, source: 'bc_media_assets' };
  }
  const res = await api.get<SavedListDto>('/media/assets', {
    source: 'upload',
    cursor: cursor ?? undefined,
    limit,
  });
  const items = (res.items ?? [])
    .map(adaptSaved)
    .filter((a) => a.source === 'upload' || a.provider === 'upload');
  return {
    items,
    nextCursor: numOrNull(res.next_cursor),
    source: strOf(res.source, 'bc_media_assets'),
  };
}

/* Same api base the typed client uses (api.ts owns this; re-derived here ONLY
 * because upload progress needs XHR, which the fetch-based client cannot report.
 * Nothing else about the request differs — same cookie session, CSRF and
 * idempotency treatment as a JSON mutation. */
const MEDIA_API_BASE = (import.meta.env.VITE_API_BASE ?? 'https://api.bookedcentral.com/v1').replace(/\/$/, '');

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export interface UploadOptions {
  /** Real byte progress; percent is null when the transport can't compute it. */
  onProgress?: (p: { loaded: number; total: number; percent: number | null }) => void;
  signal?: AbortSignal;
}

/**
 * Upload the user's OWN media (image/logo/video) to POST /v1/media/upload and
 * return the saved workspace asset. Raw-bytes body (mirrors the Brand-Kit logo
 * uploader), workspace-scoped by the session — no workspace param is sent. Uses
 * XMLHttpRequest so real upload progress can be reported; errors are normalised
 * to ApiError so the house envelope + honest backend message flow through
 * unchanged. The server is authoritative on type/size/quota.
 */
export function uploadOwnMedia(file: File, opts: UploadOptions = {}): Promise<SaveResult> {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Simulate honest incremental progress in dev.
      let loaded = 0;
      const total = file.size || 1;
      const tick = () => {
        loaded = Math.min(total, loaded + Math.ceil(total / 4));
        opts.onProgress?.({ loaded, total, percent: Math.round((loaded / total) * 100) });
        if (loaded < total) {
          setTimeout(tick, 60);
        } else {
          mockSeq += 1;
          const kind: MediaKind = file.type.startsWith('video/') ? 'video' : 'photo';
          const asset: SavedAsset = {
            id: mockSeq,
            provider: 'upload',
            providerAssetId: `upload_${mockSeq}`,
            kind,
            rendition: 'original',
            sourceUrl: null,
            photographer: null,
            photographerUrl: null,
            attribution: null,
            storageRef: `workspace-mock/upload_${mockSeq}`,
            mime: file.type || 'application/octet-stream',
            bytes: file.size,
            width: null,
            height: null,
            createdAt: new Date().toISOString(),
            source: 'upload',
            originalFilename: file.name,
            previewUrl: null,
          };
          MOCK_SAVED = [asset, ...MOCK_SAVED];
          resolve({ asset, deduped: false });
        }
      };
      setTimeout(tick, 60);
    });
  }

  return new Promise<SaveResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${MEDIA_API_BASE}/media/upload`, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    const csrf = readCookie('bc_csrf');
    if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf);
    // Fresh key: each upload is a distinct write (a deliberate retry is a new file pick).
    xhr.setRequestHeader('X-Bksesh-Idempotency-Key', crypto.randomUUID());
    // Original filename as a header hint (display-only; server derives storage ref).
    try {
      xhr.setRequestHeader('X-Bksesh-Filename', encodeURIComponent(file.name));
    } catch {
      /* header rejected by the browser — harmless, server has a fallback */
    }

    if (opts.signal) {
      if (opts.signal.aborted) {
        xhr.abort();
        reject(new ApiError(0, 'aborted', 'Upload cancelled.'));
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.upload.onprogress = (e) => {
      opts.onProgress?.({
        loaded: e.loaded,
        total: e.total,
        percent: e.lengthComputable && e.total > 0 ? Math.round((e.loaded / e.total) * 100) : null,
      });
    };

    xhr.onerror = () => reject(new ApiError(0, 'network_error', 'Upload didn’t complete (connection or size). Check the file and try again.'));
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'Upload cancelled.'));

    xhr.onload = () => {
      const text = xhr.responseText || '';
      let payload: Record<string, unknown> | undefined;
      try {
        payload = text ? (JSON.parse(text) as Record<string, unknown>) : undefined;
      } catch {
        payload = undefined;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        const dto = (payload as UploadResponseDto | undefined) ?? {};
        resolve({ asset: adaptSaved(dto.asset ?? {}), deduped: boolOf(dto.deduped) });
        return;
      }
      // House error envelope: nested {error:{code,message}} or flat {code,message,data}.
      const nested =
        payload && typeof payload.error === 'object' && payload.error !== null
          ? (payload.error as Record<string, unknown>)
          : undefined;
      const code = (nested?.code as string) ?? (payload?.code as string) ?? 'error';
      const message = (nested?.message as string) ?? (payload?.message as string) ?? xhr.statusText;
      reject(new ApiError(xhr.status, code, message, nested?.details ?? payload?.data ?? payload?.details));
    };

    xhr.send(file);
  });
}

/** Pick a sensible default rendition to save: photo prefers 'large' then
 *  'original'; video prefers the largest-area rendition. Honest fallback = the
 *  first available key. */
export function defaultRendition(item: StockItem): string {
  if (item.renditions.length === 0) return 'large';
  if (item.kind === 'photo') {
    const pref = ['large', 'large2x', 'original', 'medium'];
    for (const key of pref) {
      const found = item.renditions.find((r) => r.key === key);
      if (found) return found.key;
    }
    return item.renditions[0].key;
  }
  const sorted = [...item.renditions].sort(
    (a, b) => (b.width ?? 0) * (b.height ?? 0) - (a.width ?? 0) * (a.height ?? 0),
  );
  return sorted[0].key;
}
